import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import PublicOrganizationService from '../../services/publicOrganizationService';
import './OrganizationPage.css';

const OrganizationPage = () => {
  const { organizationId } = useParams();
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchOrganization = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const result = await PublicOrganizationService.getPublicOrganizationById(organizationId);
        const formattedOrganization = PublicOrganizationService.formatOrganization(result);
        setOrganization(formattedOrganization);
      } catch (err) {
        console.error('Error fetching organization:', err);
        setError('No se pudo cargar la información de la organización.');
      } finally {
        setLoading(false);
      }
    };

    if (organizationId) {
      fetchOrganization();
    }
  }, [organizationId]);

  const handleEmailClick = (email) => {
    window.location.href = `mailto:${email}`;
  };

  const handlePhoneClick = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    window.location.href = `tel:+${cleanPhone}`;
  };

  const handleAddressClick = (address) => {
    const addressForMaps = PublicOrganizationService.getAddressForMaps(address);
    if (addressForMaps) {
      const encodedAddress = encodeURIComponent(addressForMaps);
      window.open(`https://maps.google.com/?q=${encodedAddress}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="organization-page-container">
        <div className="organization-page-header">
          <div className="header-content">
            <Link to="/directory" className="back-link">
              <i className="fas fa-arrow-left"></i>
              Volver al directorio
            </Link>
          </div>
        </div>

        <div className="loading-state">
          <div className="loading-spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="organization-page-container">
        <div className="organization-page-header">
          <div className="header-content">
            <Link to="/directory" className="back-link">
              <i className="fas fa-arrow-left"></i>
              Volver al directorio
            </Link>
          </div>
        </div>

        <div className="error-state">
          <div className="error-icon">
            <i className="fas fa-exclamation-circle"></i>
          </div>
          <h3>Error al cargar</h3>
          <p>{error}</p>
          <Link to="/directory" className="btn btn-primary">
            <i className="fas fa-arrow-left"></i>
            Volver al directorio
          </Link>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="organization-page-container">
        <div className="organization-page-header">
          <div className="header-content">
            <Link to="/directory" className="back-link">
              <i className="fas fa-arrow-left"></i>
              Volver al directorio
            </Link>
          </div>
        </div>

        <div className="error-state">
          <div className="error-icon">
            <i className="fas fa-search"></i>
          </div>
          <h3>Clínica no encontrada</h3>
          <p>La clínica que buscas no existe o no está disponible públicamente.</p>
          <Link to="/directory" className="btn btn-primary">
            <i className="fas fa-arrow-left"></i>
            Volver al directorio
          </Link>
        </div>
      </div>
    );
  }

  const hasAddress = organization.address && 
    (typeof organization.address === 'string' || 
     (typeof organization.address === 'object' && 
      (organization.address.street || organization.address.city || organization.address.state)));

  return (
    <div className="organization-page-container">
      {/* Main Content */}
      <div className="organization-page-content">
        <div className="organization-hero">
          <div className="hero-content">
            <div className="organization-icon-large">
              <i className="fas fa-clinic-medical"></i>
            </div>
            <h1 className="organization-title">{organization.displayName}</h1>
            <p className="organization-subtitle">Clínica dental profesional</p>
          </div>
        </div>

        <div className="organization-details">
          <div className="details-grid">
            {/* Contact Information */}
            <div className="detail-section">
              <h2 className="section-title">
                <i className="fas fa-address-book"></i>
                Información de Contacto
              </h2>
              
              <div className="contact-details">
                {hasAddress && (
                  <div className="contact-item-large">
                    <div className="contact-icon-large">
                      <i className="fas fa-map-marker-alt"></i>
                    </div>
                    <div className="contact-info-large">
                      <h3 className="contact-label-large">Dirección</h3>
                      <button 
                        className="contact-value-large clickable" 
                        onClick={() => handleAddressClick(organization.address)}
                        title="Ver en Google Maps"
                      >
                        {organization.displayAddress}
                      </button>
                      <p className="contact-hint">Haz clic para ver en Google Maps</p>
                    </div>
                  </div>
                )}

                {organization.phoneNumber && (
                  <div className="contact-item-large">
                    <div className="contact-icon-large">
                      <i className="fas fa-phone"></i>
                    </div>
                    <div className="contact-info-large">
                      <h3 className="contact-label-large">Teléfono</h3>
                      <button 
                        className="contact-value-large clickable" 
                        onClick={() => handlePhoneClick(organization.phoneNumber)}
                        title="Llamar"
                      >
                        {organization.displayPhone}
                      </button>
                      <p className="contact-hint">Haz clic para llamar</p>
                    </div>
                  </div>
                )}

                {organization.email && (
                  <div className="contact-item-large">
                    <div className="contact-icon-large">
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div className="contact-info-large">
                      <h3 className="contact-label-large">Email</h3>
                      <button 
                        className="contact-value-large clickable" 
                        onClick={() => handleEmailClick(organization.email)}
                        title="Enviar email"
                      >
                        {organization.displayEmail}
                      </button>
                      <p className="contact-hint">Haz clic para enviar un email</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Services */}
            {organization.displayServices && organization.displayServices.length > 0 && (
              <div className="detail-section">
                <h2 className="section-title">
                  <i className="fas fa-tooth"></i>
                  Servicios Disponibles
                </h2>
                
                <div className="services-grid">
                  {organization.displayServices.map((service, index) => (
                    <div key={index} className="service-card">
                      <div className="service-icon">
                        <i className="fas fa-check-circle"></i>
                      </div>
                      <span className="service-name">{service}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Social Media Links */}
            {organization.displaySocialMediaLinks && organization.displaySocialMediaLinks.length > 0 && (
              <div className="detail-section">
                <h2 className="section-title">
                  <i className="fas fa-share-alt"></i>
                  Síguenos en Redes Sociales
                </h2>
                
                <div className="social-media-grid">
                  {organization.displaySocialMediaLinks.map((link, index) => (
                    <a 
                      key={index} 
                      href={link.url} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="social-media-card"
                      title={`Visitar ${link.displayName}`}
                    >
                      <div className="social-media-icon">
                        <i className={link.iconClass}></i>
                      </div>
                      <div className="social-media-info">
                        <span className="social-media-name">{link.displayName}</span>
                        {link.displayText && (
                          <span className="social-media-text">{link.displayText}</span>
                        )}
                      </div>
                      <div className="social-media-arrow">
                        <i className="fas fa-external-link-alt"></i>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            {organization.email && (
              <button 
                className="btn btn-primary action-btn"
                onClick={() => handleEmailClick(organization.email)}
              >
                <i className="fas fa-envelope"></i>
                Contactar por Email
              </button>
            )}
            
            {organization.phoneNumber && (
              <button 
                className="btn btn-secondary action-btn"
                onClick={() => handlePhoneClick(organization.phoneNumber)}
              >
                <i className="fas fa-phone"></i>
                Llamar Ahora
              </button>
            )}
            
            {hasAddress && (
              <button 
                className="btn btn-primary action-btn"
                onClick={() => handleAddressClick(organization.address)}
              >
                <i className="fas fa-map-marker-alt"></i>
                Ver Ubicación
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="organization-page-footer">
        <p>
          ¿Eres el propietario de esta clínica? 
          <a href="mailto:dentaris@digitaport.com" className="footer-link">
            Contáctanos para actualizar tu información
          </a>
        </p>
      </div>
    </div>
  );
};

export default OrganizationPage;